/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { useDeepCompareMemo } from "use-deep-compare";
import { ToolbarCommandButton } from "../../../../ui/components/Toolbar/ToolbarCommandButton";
import { IntegrationEditorEdit } from "../IntegrationEditorEdit";
import { useStateWithEvent, useViewInfo } from "../../../../ui/hooks";
import { useResourceInfo } from "../../../../ui/hooks/useResourceInfo";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { ToolbarNote } from "../../../../ui/components/Toolbar/ToolbarNote";
import { DateTimeAgo } from "../../../../ui/components/DateTimeAgo";
import { termsEditorCommon } from "../../../../terms/common";
import { DROPDOWN_STYLE } from "../../../../ui/components/DropdownMenu";
import { ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { useLayoutElement } from "../../../../ui/components/Layout/useLayoutElement";
import { LayoutState_Column } from "../../../../ui/components/Layout/LayoutState";

export interface IIntegrationEditorToolbarProps {
	className?: string;
	view: IntegrationEditorEdit;
}

export const IntegrationEditorToolbar: React.FC<IIntegrationEditorToolbarProps> = ({ className, view }) => {
	const t = useTranslate();

	const baseClass = BEM("edt-integration-editor-toolbar");

	const layout = view.getLayout();
	const viewInfo = useViewInfo(view);
	const resourceInfo = useResourceInfo(view);

	const panelLeft = useLayoutElement<LayoutState_Column>(layout.getById("panelLeft"));

	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [viewInfo, resourceInfo]);

	const undoRedoEnabled = useStateWithEvent(
		view.getIntegrationModel().onModelChange,
		() => ({
			undo: view.canUndo(),
			redo: view.canRedo()
		}),
		[view]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			<ToolbarButton
				icon="mdi/dock-left"
				active={panelLeft.isExpanded}
				onClick={panelLeft.toggleExpanded}
				tooltip="Toggle Sidebar"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/undo-variant"
				commandId="integrationEditor.edit.undo"
				disabled={!undoRedoEnabled.undo}
				tooltip="Undo"
			/>
			<ToolbarCommandButton
				icon="mdi/redo-variant"
				commandId="integrationEditor.edit.redo"
				disabled={!undoRedoEnabled.redo}
				tooltip="Redo"
			/>

			<ToolbarSpacer grow={1} />

			<ToolbarNote className={baseClass.element("save-info").toClassName()}>
				<DateTimeAgo
					date={resourceInfo.resource.metaData.modifiedAt}
					valueTerm={
						resourceInfo.isBeingSaved
							? t("editor", "Saving")
							: viewInfo.isModified
							? t("editor", "Modified")
							: t("editor", "Saved")
					}
					tooltipTerm={termsEditorCommon.resourceInfo.savedAtBy}
					tVars={{ by: resourceInfo.resource.metaData.modifiedBy }}
				/>
			</ToolbarNote>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId="integrationEditor.edit.saveResource"
				disabled={!saveAllowed}
				tooltip="Save"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/menu"
				tooltip={t("editor", "More options")}
				menuItems={[
					{
						id: "rename",
						label: "Rename",
						icon: "mdi/pencil",
						commandId: "resource.rename",
						commandArgs: [resourceInfo.resource?.id]
					},
					{
						id: "move",
						label: "Move",
						icon: "mdi/folder-move",
						commandId: "resource.moveDialog.open",
						commandArgs: [resourceInfo.resource?.id]
					},
					{
						id: "duplicate",
						label: "Duplicate",
						icon: "mdi/content-copy",
						commandId: "resource.duplicate",
						commandArgs: [resourceInfo.resource?.id, true]
					},
					{
						id: "history",
						label: "Revision History",
						icon: "mdi/history",
						commandId: "resource.openRevisionHistory",
						commandArgs: [resourceInfo.resource?.id],
						disabled: true
					},
					{
						id: "delete",
						label: "Delete",
						icon: "mdi/delete",
						commandId: "resource.delete",
						commandArgs: [resourceInfo.resource?.id]
					}
				]}
				menuStyle={DROPDOWN_STYLE.COMPACT}
			/>
		</>
	);
};
